/**
 * BadgePendientes - Muestra resumen de movimientos de tesorería
 * Componente simple que muestra:
 * - Total de movimientos
 * - Cuántos están pendientes de completar
 * - Cuántos están completados
 * - Cuántos están exportados
 * - Porcentaje de completitud
 */
import React from 'react';
import {
  Paper, Box, Typography, Grid, LinearProgress, Chip, CircularProgress
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

const BadgePendientes = ({ pendientes, loading = false, error = null }) => {
  if (loading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
          <CircularProgress size={40} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="error" />
          <Typography color="error">{error}</Typography>
        </Box>
      </Paper>
    );
  }

  if (!pendientes) {
    return null;
  }

  const {
    total_movimientos,
    pendientes_completar,
    completados,
    exportados,
    porcentaje_completado
  } = pendientes;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Resumen de Movimientos
      </Typography>

      {/* Barra de progreso */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">
            Completados / Exportados
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {porcentaje_completado.toFixed(2)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={porcentaje_completado}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: porcentaje_completado === 100 ? '#4caf50' : '#2196f3'
            }
          }}
        />
      </Box>

      {/* Grid de estadísticas */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
              {total_movimientos}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Total
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
              {pendientes_completar}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Pendientes
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
              {completados}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Completados
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
              {exportados}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Exportados
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Chips de estado */}
      <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {pendientes_completar > 0 && (
          <Chip
            icon={<InfoIcon />}
            label={`${pendientes_completar} falta completar`}
            color="warning"
            variant="outlined"
          />
        )}
        {completados > 0 && (
          <Chip
            label={`${completados} listos para exportar`}
            color="info"
            variant="outlined"
          />
        )}
        {exportados > 0 && (
          <Chip
            label={`${exportados} ya exportados`}
            color="success"
            variant="outlined"
          />
        )}
        {porcentaje_completado === 100 && (
          <Chip
            label="100% completado ✓"
            color="success"
            variant="filled"
          />
        )}
      </Box>
    </Paper>
  );
};

export default BadgePendientes;
